import React from 'react';

interface PaginationProps {
  currentPage: number;
  hasNextPage: boolean;
  displayItems: any[];
  searchQuery: string;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  getTotalPages: () => number | null;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  hasNextPage,
  displayItems,
  searchQuery,
  pageSize,
  onPageChange,
  onPageSizeChange,
  getTotalPages
}) => {
  if (!(currentPage > 0 || hasNextPage || displayItems.length > 0)) {
    return null;
  }

  return (
    <div className="bg-gray-50 border-t border-gray-200 px-8 py-6 flex-shrink-0">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-2" style={{ color: '#6B7280', fontSize: '14px' }}>
            <svg className="w-4 h-4" style={{ color: '#9CA3AF' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="font-medium">
              {displayItems.length > 0 && `${displayItems.length} items ${searchQuery ? 'found' : 'shown'}`}
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <span style={{ color: '#6B7280', fontSize: '14px' }} className="font-medium">Show:</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200"
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#F96423';
                e.currentTarget.style.boxShadow = `0 0 0 2px rgba(249, 100, 35, 0.2)`;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span style={{ color: '#6B7280', fontSize: '14px' }}>per page</span>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 0}
            className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
              currentPage === 0
                ? 'cursor-not-allowed'
                : 'bg-white border hover:bg-gray-50 hover:border-gray-300 shadow-sm'
            }`}
            style={{ 
              color: currentPage === 0 ? '#9CA3AF' : '#4B5563',
              backgroundColor: currentPage === 0 ? '#f3f4f6' : 'white',
              borderColor: currentPage === 0 ? '#D3D3D3' : '#e5e7eb'
            }}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </button>
          <span className="text-sm font-medium" style={{ color: '#4B5563' }}>
            {(() => {
              const totalPages = getTotalPages();
              return totalPages 
                ? `Page ${currentPage + 1} of ${totalPages}` 
                : `Page ${currentPage + 1}`;
            })()}
          </span>
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={!hasNextPage}
            className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 shadow-sm hover:shadow-md ${
              !hasNextPage
                ? 'cursor-not-allowed'
                : 'bg-white border hover:bg-gray-50 hover:border-gray-300'
            }`}
            style={{ 
              backgroundColor: !hasNextPage ? '#f3f4f6' : 'white',
              color: !hasNextPage ? '#9CA3AF' : '#4B5563',
              borderColor: !hasNextPage ? '#D3D3D3' : '#e5e7eb'
            }}
          >
            Next
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
